import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
    ScrollView,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { getStorage, setStorage } from '../storage';
import { useScale, ScaleFn , useDarkMode} from './common';

const FAQ_API = 'https://moniter.smartdiaper.kr/api/customer-faq';

const DARK = {
    bg: '#0F172A', card: '#1E293B', cardInner: '#0F172A',
    border: 'rgba(99,179,237,0.12)', text: '#E2E8F0', textSub: '#94A3B8', muted: '#a8adb497',
};
const LIGHT = {
    bg: '#F9FAFB', card: '#FFFFFF', cardInner: '#F3F4F6',
    border: '#E5E7EB', text: '#111827', textSub: '#4B5563', muted: '#9CA3AF',
};
const S = { accent: '#38BDF8' };

const CATEGORIES = ['전체', '제품 문의', '기술 지원', '계정/결제', '기능 요청', '기타'];

export default function FAQ({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const { dark, setDark } = useDarkMode();
    const [list, setList] = useState<any[]>([]);
    const [category, setCategory] = useState('전체');
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [loading, setLoading] = useState(true);

    const T = dark ? DARK : LIGHT;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams();
            if (category && category !== '전체') qs.set('category', category);
            if (query.trim()) qs.set('q', query.trim());
            const url = FAQ_API + (qs.toString() ? '?' + qs.toString() : '');
            const res = await fetch(url);
            const data = await res.json();
            setList(data?.result || []);
        } catch {
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [category, query]);

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    useEffect(() => {
        if (!isFocused) return;
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [isFocused, load]);

    const toggle = (idx: number) => {
        setExpanded(prev => {
            const next = { ...prev, [idx]: !prev[idx] };
            if (!prev[idx]) {
                fetch(`${FAQ_API}/${idx}/view`, { method: 'POST' }).catch(() => {});
            }
            return next;
        });
    };

    return (
        <ScrollView style={{ flex: 1, backgroundColor: T.bg }} contentContainerStyle={{ padding: s(24) }}>
            <View style={[styles.cardWrap, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* 헤더 */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>자주 묻는 질문 (FAQ)</Text>
                        {list?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={load}
                        >
                            <Ionicons name="refresh" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>

                {/* 검색 */}
                <View style={[styles.searchBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <Ionicons name="search" size={s(16)} color={T.muted} style={{ marginLeft: s(12) }} />
                    <TextInput
                        placeholder="궁금한 내용을 검색하세요"
                        placeholderTextColor={T.muted}
                        value={query}
                        onChangeText={setQuery}
                        style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(10), height: '100%', outlineStyle: 'none' } as any}
                    />
                    {query ? (
                        <TouchableOpacity onPress={() => setQuery('')} style={{ paddingHorizontal: s(12) }}>
                            <AntDesign name="closecircle" size={s(14)} color={T.muted} />
                        </TouchableOpacity>
                    ) : null}
                </View>

                {/* 카테고리 탭 */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: s(8), marginBottom: s(18) }}>
                    {CATEGORIES.map(c => {
                        const active = category === c;
                        return (
                            <TouchableOpacity
                                key={c}
                                onPress={() => setCategory(c)}
                                style={{
                                    paddingHorizontal: s(14),
                                    paddingVertical: s(8),
                                    borderRadius: s(20),
                                    borderWidth: 1,
                                    borderColor: active ? S.accent : T.border,
                                    backgroundColor: active ? S.accent + '18' : T.cardInner,
                                }}
                            >
                                <Text style={{ fontSize: s(13), fontWeight: active ? '700' : '500', color: active ? S.accent : T.textSub }}>{c}</Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {/* 리스트 */}
                {loading ? (
                    <Text style={{ textAlign: 'center', color: T.muted, padding: s(40) }}>불러오는 중...</Text>
                ) : list.length === 0 ? (
                    <Text style={{ textAlign: 'center', color: T.muted, padding: s(40) }}>
                        {query ? '검색 결과가 없습니다.' : '등록된 FAQ가 없습니다.'}
                    </Text>
                ) : (
                    <View style={{ gap: s(8) }}>
                        {list.map(item => {
                            const open = !!expanded[item.IDX];
                            return (
                                <View key={item.IDX} style={[styles.faqItem, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                                    <TouchableOpacity onPress={() => toggle(item.IDX)} style={styles.faqHeader}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: s(10) }}>
                                            <View style={{ backgroundColor: S.accent, width: s(26), height: s(26), borderRadius: s(13), justifyContent: 'center', alignItems: 'center' }}>
                                                <Text style={{ color: '#fff', fontSize: s(13), fontWeight: '700' }}>Q</Text>
                                            </View>
                                            {item.CATEGORY ? (
                                                <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(8) }}>
                                                    <Text style={{ fontSize: s(11), fontWeight: '700', color: S.accent }}>{item.CATEGORY}</Text>
                                                </View>
                                            ) : null}
                                            <Text style={{ flex: 1, fontSize: s(14), fontWeight: '600', color: T.text }} numberOfLines={open ? undefined : 2}>
                                                {item.QUESTION}
                                            </Text>
                                        </View>
                                        <AntDesign name={open ? 'up' : 'down'} size={s(14)} color={T.muted} />
                                    </TouchableOpacity>
                                    {open && (
                                        <View style={[styles.faqBody, { borderTopColor: T.border }]}>
                                            <View style={{ flexDirection: 'row', gap: s(10) }}>
                                                <View style={{ backgroundColor: T.muted, width: s(26), height: s(26), borderRadius: s(13), justifyContent: 'center', alignItems: 'center' }}>
                                                    <Text style={{ color: '#fff', fontSize: s(13), fontWeight: '700' }}>A</Text>
                                                </View>
                                                <Text style={{ flex: 1, fontSize: s(14), color: T.text, lineHeight: s(22) }}>
                                                    {item.ANSWER}
                                                </Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </View>
                )}
            </View>
        </ScrollView>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    cardWrap: {
        borderRadius: s(16), borderWidth: 0.5, overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: s(20), elevation: 3,
        paddingTop: s(20), paddingHorizontal: s(24), paddingBottom: s(24),
    },
    TopBox: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: s(16) },
    iconBtn: { width: s(34), height: s(34), borderRadius: s(10), justifyContent: 'center', alignItems: 'center' },
    searchBox: {
        flexDirection: 'row', alignItems: 'center',
        borderWidth: 1, borderRadius: s(10), height: s(42), marginBottom: s(14),
    },
    faqItem: { borderWidth: 1, borderRadius: s(12), overflow: 'hidden' },
    faqHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: s(14), gap: s(10) },
    faqBody: { padding: s(14), borderTopWidth: 1 },
});
